import { matchPath } from "react-router-dom";

/** Session key consumed by private routes after a successful login. */
export const POST_LOGIN_REDIRECT_KEY = "postLoginRedirect";

/** Optional encoded user id from an email link, used to confirm the signed-in user. */
export const POST_LOGIN_USER_ID_KEY = "urlUserId";

/**
 * Private paths declared only in the route table, not on `constants.routes`.
 * Email links to those screens still resume after login.
 */
const EXTRA_PROTECTED_ROUTE_PATTERNS = [
  "/app",
  "/office/:officeId/staff",
  "/manage-content",
  "/subscription-management",
  "/enterprise-plans",
  "/owners/:id",
  "/edit-plan/:id",
  "/demo-request",
  "/broadcast-message-detail/:id",
  "/manage-testimonial",
  "/change-card-detail/:id",
];

/** Logged-out marketing and patient screens. These must not start a login redirect. */
const PUBLIC_EXACT_PATTERNS = [
  "/",
  "/authenticate",
  "/404",
  "/dentist",
  "/events",
  "/overview",
  "/patient",
  "/physician",
  "/pharmacist",
  "/personnel",
  "/supplier",
  "/healthcare-enterprise",
  "/products",
  "/pricing",
  "/features",
  "/appointment-confirmation/:appointment_id",
];

/**
 * @param {unknown} node
 * @param {string[]} acc
 * @returns {string[]}
 */
function collectRoutePatterns(node, acc) {
  if (typeof node === "string") {
    if (node.startsWith("/") && node !== "/") {
      acc.push(node);
    }
    return acc;
  }
  if (node && typeof node === "object") {
    Object.values(node).forEach((value) => collectRoutePatterns(value, acc));
  }
  return acc;
}

/**
 * @param {string} pathname
 * @param {string[]} patterns
 * @returns {boolean}
 */
export function pathMatchesRouteList(pathname, patterns) {
  return patterns.some((pattern) => {
    if (typeof pattern !== "string" || !pattern.startsWith("/")) {
      return false;
    }
    return (
      matchPath(pathname, {
        path: pattern,
        exact: true,
        strict: false,
      }) != null
    );
  });
}

/**
 * @param {string} pathname
 * @param {string[]} protectedPatterns
 * @param {string[]} publicPatterns
 * @returns {boolean}
 */
export function isProtectedPath(pathname, protectedPatterns, publicPatterns) {
  if (!pathname || typeof pathname !== "string") {
    return false;
  }
  const path = pathname.split("?")[0].split("#")[0];
  if (!path.startsWith("/") || pathMatchesRouteList(path, publicPatterns)) {
    return false;
  }
  return pathMatchesRouteList(path, protectedPatterns);
}

let cachedPatterns = null;

/**
 * Route lists are loaded on first use so this module does not import `constants`
 * (that module pulls in `uuid`, which Jest cannot parse) until a deep link is checked.
 *
 * @returns {{ protectedPatterns: string[], publicPatterns: string[] }}
 */
function getPatterns() {
  if (cachedPatterns) {
    return cachedPatterns;
  }
  // Relative require: the module name "constants" is Node's builtin outside Webpack.
  const constantsModule = require("../constants");
  const constants = constantsModule.default || constantsModule;
  const { routes } = constants;
  const publicFromRoutes = [
    routes.faq,
    routes.contact,
    routes.aboutMiraxis,
    routes.privacyPolicy,
    routes.termsAndConditions,
    routes.privacyPolicyPatient,
    routes.termsAndConditionsPatient,
    routes.privacyPolicyVendor,
    routes.termsAndConditionsVendor,
    routes.appointementConfirmationForNonMiraxis,
    routes.requestAnAppointment,
    routes.doctors,
    routes.searchDoctorBySpecialty,
    routes.doctor,
    routes.watingListRequest,
    routes.questionnaireForm,
    routes.setLocation,
    routes.doctorOffices,
    routes.doctorReviews,
    routes.downloadIcs,
    routes.paymentfailed,
    routes.findDoctors,
    routes.officeStaffs,
    ...constants.OnlineFaqPages,
  ];
  cachedPatterns = {
    protectedPatterns: [
      ...new Set([
        ...collectRoutePatterns(routes, []),
        ...EXTRA_PROTECTED_ROUTE_PATTERNS,
      ]),
    ],
    publicPatterns: [...PUBLIC_EXACT_PATTERNS, ...publicFromRoutes],
  };
  return cachedPatterns;
}

/**
 * True when `pathname` is an authenticated app screen (policy details, events, and the rest).
 * Public marketing and patient pages return false so they keep their current logged-out behavior.
 *
 * @param {string} pathname
 * @returns {boolean}
 */
export function isProtectedDeepLink(pathname) {
  const { protectedPatterns, publicPatterns } = getPatterns();
  return isProtectedPath(pathname, protectedPatterns, publicPatterns);
}

/**
 * In-app return path only. Query strings may contain any characters; the pathname may not be an absolute URL.
 *
 * @param {string} path
 * @returns {boolean}
 */
export function isSafeAppReturnPath(path) {
  if (
    typeof path !== "string" ||
    !path.startsWith("/") ||
    path.startsWith("//") ||
    path.startsWith("/\\")
  ) {
    return false;
  }
  const pathname = path.split("?")[0].split("#")[0];
  return pathname.startsWith("/") && !pathname.includes("://");
}

/**
 * Saves the email (or other deep-link) URL so login can return to it.
 * When the link has no `userId`, a previously stored id is cleared so it cannot block the return.
 *
 * @param {string} pathname
 * @param {string} [search]
 * @param {string} [hash]
 * @returns {string}
 */
export function rememberPostLoginRedirect(pathname, search = "", hash = "") {
  if (!isSafeAppReturnPath(pathname)) {
    return "";
  }
  const fullPath = `${pathname}${search || ""}${hash || ""}`;
  sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, fullPath);
  const userId = new URLSearchParams(search || "").get("userId");
  if (userId) {
    sessionStorage.setItem(POST_LOGIN_USER_ID_KEY, userId);
  } else {
    sessionStorage.removeItem(POST_LOGIN_USER_ID_KEY);
  }
  return fullPath;
}
